// As regras @font-face agora vivem em src/styles.css (um único CSS na cadeia
// crítica). Aqui só os arquivos woff2 da primeira tela, para preload.
import nunito400 from "@fontsource/nunito/files/nunito-latin-400-normal.woff2?url";
import nunito700 from "@fontsource/nunito/files/nunito-latin-700-normal.woff2?url";
import poppins700 from "@fontsource/poppins/files/poppins-latin-700-normal.woff2?url";

import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { RevealedCouponsProvider } from "@/contexts/RevealedCouponsContext";
import { LocaleProvider } from "@/i18n/LocaleProvider";
import ScrollToTop from "@/components/ScrollToTop";
import NavigationProgress from "@/components/common/NavigationProgress";
import NotFound from "@/pages/NotFound";
import { getConsent, getRegion } from "@/lib/consent";
import { loadAdsenseScript } from "@/lib/adsense";
import { loadMarketingTags, loadMarketingTagsNow } from "@/lib/marketingTags";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import { listenForForegroundPush } from "@/lib/pushClient";
import appCss from "../styles.css?url";

// Toasters e UI não-crítica fora do caminho do primeiro paint (como no App.tsx original)
const Toaster = lazy(() => import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));
const PushPrompt = lazy(() =>
  import("@/components/push/PushPrompt").then((m) => ({ default: m.PushPrompt })),
);
const CookieBanner = lazy(() =>
  import("@/components/consent/CookieBanner").then((m) => ({ default: m.CookieBanner })),
);
const SophiaBubble = lazy(() => import("@/components/sophia/SophiaBubble"));

const SOCIAL_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/pOOf9Nrh78WhUebLRzOJgp5GbQl2/social-images/social-1779720587727-Design_sem_nome_-_2026-05-25T114758.465.webp";
const DESCRIPTION =
  "Ajude projetos sociais através do Shopping do Bem. O Cashback solidário garante que toda compra vire doação.";
const TITLE = "Shopping do Bem | Plataforma de Cashback Solidário";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      { name: "theme-color", content: "#F9BC05" },
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "author", content: "Shopping do Bem" },
      {
        name: "keywords",
        content: "doação, compras online, cashback solidário, causas sociais, instituições, ONGs",
      },
      { name: "robots", content: "index, follow" },
      { name: "mitgo-verification", content: "3c7e8ed6-d1a5-4aa3-a1eb-3591a3f6e233" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:image", content: SOCIAL_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: SOCIAL_IMAGE },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://mcruqmycffklzdqjuwwq.supabase.co", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://storage.googleapis.com" },
      { rel: "preconnect", href: "https://m.media-amazon.com" },
      { rel: "dns-prefetch", href: "https://images.weserv.nl" },
      { rel: "dns-prefetch", href: "https://connect.facebook.net" },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      { rel: "manifest", href: "/manifest.webmanifest" },
    ],
    scripts: [
      {
        // GTM + Meta Pixel: apenas stubs. O carregamento real acontece após
        // consent LGPD = "granted" (loadMarketingTags), como antes da migração.
        children:
          "window.dataLayer = window.dataLayer || [];" +
          "window.gtag = window.gtag || function(){ window.dataLayer.push(arguments); };" +
          // Consent Mode regional: EEE/UK/CH negado até aceite; demais regiões
          // (Brasil) medem por padrão, sem personalização. Escolha salva vence.
          "(function(){var c=null;try{c=localStorage.getItem('consent_marketing');}catch(e){}" +
          "var S=['AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE','IS','LI','NO','GB','CH'];" +
          "if(c==='granted'){window.gtag('consent','default',{ad_storage:'granted',ad_user_data:'granted',ad_personalization:'granted',analytics_storage:'granted'});}" +
          "else if(c==='denied'){window.gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',analytics_storage:'denied'});}" +
          "else{window.gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',analytics_storage:'denied',region:S,wait_for_update:500});" +
          "window.gtag('consent','default',{ad_storage:'granted',ad_user_data:'granted',ad_personalization:'denied',analytics_storage:'granted'});}})();" +
          "window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });" +
          "(function(){var n = window.fbq = function(){ n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };" +
          "if (!window._fbq) window._fbq = n;" +
          "n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];})();",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: RootErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="pt-BR" suppressHydrationWarning>
      <head>
        {/* As 3 fontes da primeira tela começam a baixar imediatamente. */}
        <link rel="preload" as="font" type="font/woff2" href={nunito400} crossOrigin="anonymous" />
        <link rel="preload" as="font" type="font/woff2" href={nunito700} crossOrigin="anonymous" />
        <link rel="preload" as="font" type="font/woff2" href={poppins700} crossOrigin="anonymous" />
        <HeadContent />
      </head>
      <body>
        {/* Google Tag Manager (noscript) */}
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-58Z34ZVK"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
            title="gtm"
          />
        </noscript>
        {/* Meta Pixel (noscript) */}
        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src="https://www.facebook.com/tr?id=1497265781878140&ev=PageView&noscript=1"
            alt=""
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [showDeferredUi, setShowDeferredUi] = useState(false);

  // Após uma publicação, abas abertas pedem arquivos da versão antiga que
  // não existem mais e a página quebra. Recarrega uma vez para pegar a nova.
  useEffect(() => {
    const onStale = (e: Event) => {
      const key = "sdb_stale_reload";
      const last = Number(sessionStorage.getItem(key) || 0);
      if (Date.now() - last < 30_000) return;
      sessionStorage.setItem(key, String(Date.now()));
      e.preventDefault();
      window.location.reload();
    };
    window.addEventListener("vite:preloadError", onStale);
    return () => window.removeEventListener("vite:preloadError", onStale);
  }, []);

  useEffect(() => {
    const tryLoad = () => {
      // Modo de consentimento do Google: fora da Europa/UK/CH o GTM carrega
      // com sinais "negado" (sem cookies) até o aceite; Meta só com aceite.
      void getRegion().then(({ strict }) => {
        if (getConsent() === "granted" || !strict) {
          loadAdsenseScript();
          loadMarketingTags();
        }
      });
    };
    // Terceiros (GTM/Meta/AdSense) só depois do LCP confirmado: nunca dentro
    // da janela da métrica. Fallback por tempo + primeira interação garantem
    // que o rastreamento sempre carrega.
    // Páginas de marca recebem anúncio: GTM sobe na hora, junto com os botões.
    if (window.location.pathname.startsWith("/marca/")) {
      void getRegion().then(({ strict }) => {
        if (getConsent() === "granted" || !strict) loadMarketingTagsNow();
      });
    }
    let done = false;
    const boot = () => {
      if (done) return;
      done = true;
      const idle =
        (window as unknown as { requestIdleCallback?: (fn: () => void, opts?: unknown) => number })
          .requestIdleCallback || ((fn: () => void) => setTimeout(fn, 300));
      idle(tryLoad, { timeout: 3000 });
    };

    let observer: PerformanceObserver | undefined;
    try {
      if (typeof PerformanceObserver !== "undefined") {
        observer = new PerformanceObserver(() => {
          // LCP candidato registrado: a partir daqui terceiros não competem mais.
          setTimeout(boot, 500);
        });
        observer.observe({ type: "largest-contentful-paint", buffered: true });
      }
    } catch {
      /* navegador sem suporte: cai nos fallbacks abaixo */
    }

    const timer = setTimeout(boot, 6000);
    const onInteract = () => boot();
    window.addEventListener("pointerdown", onInteract, { once: true, passive: true });
    window.addEventListener("keydown", onInteract, { once: true });
    window.addEventListener("consent-change", tryLoad);
    return () => {
      observer?.disconnect();
      clearTimeout(timer);
      window.removeEventListener("pointerdown", onInteract);
      window.removeEventListener("keydown", onInteract);
      window.removeEventListener("consent-change", tryLoad);
    };
  }, []);

  // UI não-crítica (toasters) montada só após o primeiro paint
  useEffect(() => {
    const id = setTimeout(() => setShowDeferredUi(true), 0);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    let stop: () => void = () => undefined;
    let cancelled = false;
    const start = () => {
      stop();
      void listenForForegroundPush()
        .then((unsubscribe) => {
          if (cancelled) unsubscribe();
          else stop = unsubscribe;
        })
        .catch((error) => console.warn("foreground push listener", error));
    };
    start();
    window.addEventListener("push-registration-change", start);
    return () => {
      cancelled = true;
      window.removeEventListener("push-registration-change", start);
      stop();
    };
  }, []);

  // Monta CookieBanner só depois da página estar idle — evita que ele seja o LCP
  useEffect(() => {
    const w = window as unknown as {
      requestIdleCallback?: (fn: () => void, opts?: unknown) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(() => setShowCookieBanner(true), { timeout: 4000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = setTimeout(() => setShowCookieBanner(true), 2500);
    return () => clearTimeout(t);
  }, []);

  // O QueryClient vive no contexto do router (SSR + hidratação); precisa estar
  // provido aqui para todos os hooks useQuery da árvore.
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <ScrollToTop />
      <AuthProvider>
        <LocaleProvider>
          <RevealedCouponsProvider>
            <TooltipProvider>
              {showDeferredUi && (
                <Suspense fallback={null}>
                  <Toaster />
                  <Sonner />
                </Suspense>
              )}
              {showCookieBanner && (
                <Suspense fallback={null}>
                  <CookieBanner />
                </Suspense>
              )}
              {showCookieBanner && (
                <Suspense fallback={null}>
                  <PushPrompt />
                </Suspense>
              )}
              {showCookieBanner && (
                <Suspense fallback={null}>
                  <SophiaBubble />
                </Suspense>
              )}
              <NavigationProgress />
              <Outlet />
            </TooltipProvider>
          </RevealedCouponsProvider>
        </LocaleProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  useEffect(() => {
    console.error(error);
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-6">
      <div className="max-w-md w-full text-center space-y-4">
        <h1 className="text-xl font-semibold">Esta página não carregou</h1>
        <p className="text-muted-foreground">
          Algo deu errado do nosso lado. Tente novamente ou volte para a página inicial.
        </p>
        <div className="flex gap-2 justify-center flex-wrap">
          <button
            type="button"
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Tentar novamente
          </button>
          <a className="px-4 py-2 rounded-md border border-border bg-card" href="/">
            Ir para a página inicial
          </a>
        </div>
      </div>
    </div>
  );
}
